import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Autocomplete,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useQuery } from '@tanstack/react-query';
import { Client } from '../../../utils/types';
import { fetchClients } from '../../../utils/helper';

import { useAuth } from "../../../context/AuthContext";

interface DeleteClientModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (clientId: string) => void;
}

export const DeleteClientModal: React.FC<DeleteClientModalProps> = ({ open, onClose, onSubmit }) => {
  const { jwt } = useAuth();

  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  // Fetch clients for autocomplete
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: () => fetchClients(jwt),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedClient?.id) {
      onSubmit(selectedClient.id);
      setSelectedClient(null); // Reset selection
      onClose();
    }
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="delete-client-modal-title">
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography id="delete-client-modal-title" variant="h6" component="h2">
            Delete Client
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={clients}
                getOptionLabel={(client) => `${client.clientName} (${client.clientEmail})`}
                value={selectedClient}
                onChange={(event, newValue) => setSelectedClient(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Client"
                    variant="outlined"
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: null,
                    }}
                  />
                )}
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedClient
                  ? `Confirm deletion of ${selectedClient.clientName}? This action cannot be undone.`
                  : 'Select a client to delete.'}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              disabled={!selectedClient}
            >
              Delete
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
